import { KERB_LANE, LANES } from '@accra/shared';
import type { Rng } from '../../core/rng';
import { TRAFFIC_KINDS, VARIANTS_PER_KIND, type TrafficKind } from '../../content/traffic';
import type { RunState, TrafficCar } from '../../game/RunState';
import { stopAt } from '../passengers/stopSequence';
import { laneX } from '../lanes';
import { nextJunction } from '../junctions/signals';

const SPAWN_AHEAD = 170;
/** Metres between rows of traffic at normal density (about 20 vehicles per km). */
const ROW_GAP = [40, 70] as const;
/** Chance a row has two vehicles side by side (there is always a free lane). */
const PAIR_CHANCE = 0.18;
const DESPAWN_BEHIND = 40;
/** Keep the kerb lane clear this close to a stop box. */
const STOP_CLEARANCE = 55;
/** Nothing spawns in the first stretch so the player can settle in. */
const GRACE_METRES = 60;

export class TrafficSpawner {
  private nextRowAt = GRACE_METRES + SPAWN_AHEAD;
  private nextId = 1;

  constructor(private readonly rng: Rng) {}

  /** Nothing is set up until the road is `SPAWN_AHEAD` beyond `s` (the rows skipped are never made). */
  holdUntil(s: number): void {
    this.nextRowAt = Math.max(this.nextRowAt, s + SPAWN_AHEAD);
  }

  update(state: RunState, density: number): void {
    const horizon = state.bus.s + SPAWN_AHEAD;
    while (this.nextRowAt < horizon) {
      this.spawnRow(state, this.nextRowAt);
      this.nextRowAt += this.rng.range(ROW_GAP[0], ROW_GAP[1]) / Math.max(0.3, density);
    }
    state.traffic = state.traffic.filter((car) => car.s > state.bus.s - DESPAWN_BEHIND);
  }

  private spawnRow(state: RunState, at: number): void {
    const junction = nextJunction(at - 20);
    // No barriers in or just before a junction.
    const nearStop = this.isNearStop(state, at) || Math.abs(junction.position - at) < 30;
    const lanes = this.pickBlockedLanes(nearStop);
    for (const lane of lanes) {
      const kind = this.pickKind(nearStop);
      state.traffic.push(this.makeCar(kind, lane, at + this.rng.range(-4, 4)));
    }
  }

  private isNearStop(state: RunState, at: number): boolean {
    for (let seq = state.stop.seq; seq < state.stop.seq + 2; seq += 1) {
      if (Math.abs(stopAt(seq).position - at) < STOP_CLEARANCE) return true;
    }
    return false;
  }

  /** One or two lanes per row, never all three: there is always a way through. */
  private pickBlockedLanes(nearStop: boolean): number[] {
    const candidates = [...Array(LANES).keys()].filter((lane) => !(nearStop && lane === KERB_LANE));
    const count = Math.min(candidates.length - 1, this.rng.next() < PAIR_CHANCE ? 2 : 1);
    const lanes: number[] = [];
    while (lanes.length < Math.max(1, count)) {
      const lane = this.rng.pick(candidates);
      if (!lanes.includes(lane)) lanes.push(lane);
    }
    return lanes;
  }

  private pickKind(nearStop: boolean): TrafficKind {
    const kinds = nearStop ? TRAFFIC_KINDS.filter((k) => !k.hoppable) : TRAFFIC_KINDS;
    return this.rng.weighted(kinds);
  }

  private makeCar(kind: TrafficKind, lane: number, s: number): TrafficCar {
    return {
      id: this.nextId++,
      kind,
      s,
      lane,
      x: laneX(lane),
      vx: 0,
      speed: this.rng.range(kind.speed[0], kind.speed[1]),
      variant: this.rng.int(0, VARIANTS_PER_KIND - 1),
      closeCallCounted: false,
    };
  }
}
